import { css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import store from '../../../store/store';
import { StoreState } from '../../../store/store.interface';
import { formatDateDefault, formattedAuthors } from '../../../common/utils/util';
import { getImageSrcUrl } from '../../../common/utils/product-util';
import { getCartItemFormat } from '../../../common/utils/cart-util';
import { expirationDate, isExpired } from '../my-digital-products/utils/my-digital-product.util';
import { OrderHistory as IOrderHistory, UserOrderDetails } from '../../order-history/order-history.interface';
import { MyDigitalProducts } from '../my-digital-products/my-digital-products';
import { getAccessType, renderAccessMessage } from '../../../common/utils/access-message-util';
import { MyDigitalSamplesAnalytics } from './my-digital-samples.analytic';
import { fetchInstructorSampleOrders } from '../thunks/fetch-instructor-sample-orders.thunk';
import { getInstructorSampleOrdersData } from '../store/my-account.selector';

const INSTRUCTOR_COMP = 'INSTRUCTOR_COMP';

@customElement('mhecomm-my-digital-samples')
export class MyDigitalSamples extends MyDigitalProducts {
  static styles = [
    ...MyDigitalProducts.styles,
    css`
      .sample-expiry-badge {
        background-color: #d9480f;
        color: #ffffff;
      }
    `,
  ];

  renderContent() {
    const totalOrders = this.digitalOrdersHistory!.length;

    return totalOrders === 0
      ? html`<p class="fs-3">You have no digital samples at this time.</p>`
      : html`
          <div class="row justify-content-sm-center">
            <div class="col-12">
              ${this.digitalOrdersHistory?.map(
                (order) => html`
                  <ul class="list-group">
                    ${order.userOrderDetails.map(
                      (item) => html`
                        <li class="d-block mt-4">
                          <div class="row p-3 align-items-start">
                            <div class="col-4 col-sm-3 col-md-2">
                              <img
                                alt=${item.productDescription}
                                class="img-fluid"
                                src=${getImageSrcUrl(item.coverImage ? item.coverImage : item.productIsbn)}
                              />
                            </div>
                            <div class="col-8 col-sm-9 col-md-7">
                              <h2 class="h4 fs-large fw-semibold mb-1">
                                <span data-automation-id="digital-sample-title-${order.webOrderNo}-${item.isbn10}">
                                  ${this.getTitle(item)}
                                </span>
                              </h2>
                              ${item.copyrightYear ? html`<div>Release Year: ${item.copyrightYear}</div>` : nothing}
                              <div>
                                <span class="fw-bold">ISBN10:</span>
                                ${item.isbn10}
                              </div>
                              ${item.authors
                                ? html`<div><span class="fw-bold">By:</span> ${formattedAuthors(item.authors)}</div>`
                                : nothing}
                              ${item.formatDescription
                                ? html`
                                    <div>
                                      <span class="fw-bold">Format:</span>
                                      ${getCartItemFormat(item.formatDescription, item.formatCode, item.virtual)}
                                    </div>
                                  `
                                : nothing}
                              ${(item.licenseEndDate || item.terms) && !isExpired(order.orderedDate, item.terms, item.licenseEndDate)
                                ? html`
                                    <div class="mt-2">
                                      <span class="badge rounded-pill sample-expiry-badge text-wrap">
                                        Expires on
                                        ${formatDateDefault(item.licenseEndDate ?? expirationDate(order.orderedDate, item.terms))}
                                      </span>
                                    </div>
                                  `
                                : nothing}
                            </div>
                            <div class="col-12 col-md-3 text-md-end px-2 px-md-0 px-xl-2 my-auto">
                              <hr class="hr-primary d-md-none" />
                              ${this.displayAccessOrLaunchButton(order, item)}
                              ${this.launchSharpen(order, item)}
                            </div>
                          </div>
                          <hr class="hr-primary m-0" />
                        </li>
                      `
                    )}
                  </ul>
                `
              )}
            </div>
          </div>
        `;
  }

  protected fetchOrders(forceUpdate = false) {
    store.dispatch(fetchInstructorSampleOrders({ ...(forceUpdate && { forceUpdate: true }) }));
  }

  protected getOrdersHistoryData(state: StoreState) {
    return getInstructorSampleOrdersData(state);
  }

  private displayAccessOrLaunchButton(order: IOrderHistory, item: UserOrderDetails) {
    const accessType = getAccessType(item);
    return accessType ? renderAccessMessage(accessType) : this._getButtonFromUserOrderDetails(order, item);
  }

  _denormalizeOrderHistory(orders: IOrderHistory[]): IOrderHistory[] {
    return orders
      .filter((order) => order.orderType?.toUpperCase() === INSTRUCTOR_COMP && !order.webOrderNo.startsWith('CODE_'))
      .map((order) => ({
        ...order,
        userOrderDetails: (order.userOrderDetails ?? []).filter((item) => item.virtual),
      }))
      .filter((order) => order.userOrderDetails.length > 0)
      .sort((a, b) => new Date(b.orderedDate).getTime() - new Date(a.orderedDate).getTime());
  }

  async _orderHistoryAnalyticsData(orders: IOrderHistory[]) {
    MyDigitalSamplesAnalytics.load({ element: this, orders });
  }

  _triggerCTAAnalyticsData(name: string, label: string, order: IOrderHistory, item: UserOrderDetails, href?: string) {
    MyDigitalSamplesAnalytics.cta({ element: this, name, label, order, item, href });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-my-digital-samples': MyDigitalSamples;
  }
}
